import React, { useState } from 'react';
import { XIcon, MapPinIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, CarIcon, GaugeIcon, FuelIcon, TrendingUpIcon, ZapIcon, UserIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { GasStation } from '../data/stations';
interface Vehicle {
  id: string;
  status: 'waiting' | 'in-service' | 'completed';
  addedBy: string;
  addedByName: string;
}
interface StatusChangeEvent {
  vehicleId: string;
  fromStatus: 'waiting' | 'in-service' | 'completed';
  toStatus: 'waiting' | 'in-service' | 'completed';
  timestamp: Date;
  stationId: string;
  userId: string;
  userName: string;
}
interface StationsOverviewProps {
  onClose: () => void;
  stations: GasStation[];
  stationQueues: Record<string, Vehicle[]>;
  statusChangeHistory: StatusChangeEvent[];
}
export function StationsOverview({
  onClose,
  stations,
  stationQueues,
  statusChangeHistory
}: StationsOverviewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRiver, setSelectedRiver] = useState<'all' | 'RG' | 'RD'>('all');
  const [selectedCommune, setSelectedCommune] = useState<string>('all');
  const [showOnlyWithQueue, setShowOnlyWithQueue] = useState(false);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const getQueueStats = (stationId: string) => {
    const queue = stationQueues[stationId] || [];
    const waiting = queue.filter(v => v.status === 'waiting').length;
    const inService = queue.filter(v => v.status === 'in-service').length;
    const total = queue.length;
    return {
      waiting,
      inService,
      total
    };
  };
  const hasFastQueue = (stationId: string): boolean => {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const recentMovements = statusChangeHistory.filter(event => event.stationId === stationId && event.fromStatus === 'waiting' && event.toStatus === 'in-service' && event.timestamp >= fiveMinutesAgo);
    return recentMovements.length > 0;
  };
  const getStationActivityLog = (stationId: string) => {
    return statusChangeHistory.filter(event => event.stationId === stationId).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 5);
  };
  // Filtrer les stations
  const filteredStations = stations.filter(station => {
    // Filtre de recherche par nom
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      const matchesName = station.name.toLowerCase().includes(search);
      const matchesLocation = station.location.toLowerCase().includes(search);
      const matchesCommune = station.commune.toLowerCase().includes(search);
      if (!matchesName && !matchesLocation && !matchesCommune) {
        return false;
      }
    }
    // Filtre par rive
    if (selectedRiver !== 'all' && station.riverSide !== selectedRiver) {
      return false;
    }
    // Filtre par commune
    if (selectedCommune !== 'all' && station.commune !== selectedCommune) {
      return false;
    }
    // Filtre par file d'attente
    if (showOnlyWithQueue) {
      const stats = getQueueStats(station.id);
      if (stats.waiting === 0) {
        return false;
      }
    }
    // Filtre par disponibilité
    if (showOnlyAvailable) {
      if (!station.fuelAvailability.essence && !station.fuelAvailability.diesel) {
        return false;
      }
    }
    return true;
  });
  const operationalStations = filteredStations.filter(s => s.isOperational).length;
  const totalVehicles = Object.values(stationQueues).reduce((sum, queue) => sum + queue.length, 0);
  const fastQueueStations = filteredStations.filter(station => {
    if (!station.isOperational) return false;
    return hasFastQueue(station.id);
  });
  const regularStations = filteredStations.filter(station => {
    if (!station.isOperational) return false;
    return !hasFastQueue(station.id);
  });
  const nonOperationalStations = filteredStations.filter(s => !s.isOperational);
  // Obtenir toutes les communes uniques
  const allCommunes = Array.from(new Set(stations.map(s => s.commune))).sort();
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-857">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden" data-id="element-858">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white" data-id="element-859">
          <div className="flex items-center justify-between mb-4" data-id="element-860">
            <div className="flex items-center gap-3" data-id="element-861">
              <MapPinIcon className="w-8 h-8" data-id="element-862" />
              <div data-id="element-863">
                <h2 className="text-2xl font-bold" data-id="element-864">
                  Vue d'Ensemble des Stations
                </h2>
                <p className="text-blue-100 text-sm" data-id="element-865">
                  Toutes les stations service de Bamako
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors" data-id="element-866">
              <XIcon className="w-6 h-6" data-id="element-867" />
            </button>
          </div>
          {/* Search and Filters */}
          <div className="space-y-4" data-id="element-868">
            {/* Search Bar */}
            <div className="relative" data-id="element-869">
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-200" data-id="element-870" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher par nom, localisation ou commune..." className="w-full pl-12 pr-4 py-3 text-gray-900 border-2 border-white border-opacity-30 rounded-xl focus:border-white focus:outline-none transition-colors bg-white bg-opacity-20 placeholder-blue-200" data-id="element-871" />
            </div>
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap" data-id="element-872">
              <div className="flex items-center gap-2" data-id="element-873">
                <FilterIcon className="w-4 h-4 text-blue-200" data-id="element-874" />
                <span className="text-sm font-semibold text-blue-100" data-id="element-875">
                  Filtres:
                </span>
              </div>
              {/* Rive Filter */}
              <select value={selectedRiver} onChange={e => setSelectedRiver(e.target.value as 'all' | 'RG' | 'RD')} className="px-4 py-2 bg-white bg-opacity-20 border-2 border-white border-opacity-30 rounded-lg text-white font-semibold focus:outline-none focus:border-white transition-colors" data-id="element-876">
                <option value="all" className="text-gray-900" data-id="element-877">
                  Toutes les Rives
                </option>
                <option value="RG" className="text-gray-900" data-id="element-878">
                  Rive Gauche
                </option>
                <option value="RD" className="text-gray-900" data-id="element-879">
                  Rive Droite
                </option>
              </select>
              {/* Commune Filter */}
              <select value={selectedCommune} onChange={e => setSelectedCommune(e.target.value)} className="px-4 py-2 bg-white bg-opacity-20 border-2 border-white border-opacity-30 rounded-lg text-white font-semibold focus:outline-none focus:border-white transition-colors" data-id="element-880">
                <option value="all" className="text-gray-900" data-id="element-881">
                  Toutes les Communes
                </option>
                {allCommunes.map(commune => <option key={commune} value={commune} className="text-gray-900" data-id="element-882">
                    {commune}
                  </option>)}
              </select>
              {/* Queue Filter */}
              <button onClick={() => setShowOnlyWithQueue(!showOnlyWithQueue)} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${showOnlyWithQueue ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white border-2 border-white border-opacity-30'}`} data-id="element-883">
                <ClockIcon className="w-4 h-4" data-id="element-884" />
                En Attente
              </button>
              {/* Availability Filter */}
              <button onClick={() => setShowOnlyAvailable(!showOnlyAvailable)} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${showOnlyAvailable ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white border-2 border-white border-opacity-30'}`} data-id="element-885">
                <FuelIcon className="w-4 h-4" data-id="element-886" />
                Carburant Disponible
              </button>
              {/* Clear Filters */}
              {(searchTerm || selectedRiver !== 'all' || selectedCommune !== 'all' || showOnlyWithQueue || showOnlyAvailable) && <button onClick={() => {
              setSearchTerm('');
              setSelectedRiver('all');
              setSelectedCommune('all');
              setShowOnlyWithQueue(false);
              setShowOnlyAvailable(false);
            }} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-colors" data-id="element-887">
                  Réinitialiser
                </button>}
            </div>
            {/* Results Count */}
            {(searchTerm || selectedRiver !== 'all' || selectedCommune !== 'all' || showOnlyWithQueue || showOnlyAvailable) && <div className="text-sm text-blue-100" data-id="element-888">
                {filteredStations.length} station
                {filteredStations.length !== 1 ? 's' : ''} trouvée
                {filteredStations.length !== 1 ? 's' : ''}
              </div>}
          </div>
          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4" data-id="element-889">
            <div className="bg-white bg-opacity-20 rounded-xl p-4" data-id="element-890">
              <div className="flex items-center gap-3" data-id="element-891">
                <div className="bg-white bg-opacity-30 p-2 rounded-lg" data-id="element-892">
                  <MapPinIcon className="w-5 h-5 text-white" data-id="element-893" />
                </div>
                <div data-id="element-894">
                  <p className="text-sm text-blue-100" data-id="element-895">
                    Stations Opérationnelles
                  </p>
                  <p className="text-2xl font-bold" data-id="element-896">
                    {operationalStations} / {filteredStations.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4" data-id="element-897">
              <div className="flex items-center gap-3" data-id="element-898">
                <div className="bg-white bg-opacity-30 p-2 rounded-lg" data-id="element-899">
                  <ZapIcon className="w-5 h-5 text-white" data-id="element-900" />
                </div>
                <div data-id="element-901">
                  <p className="text-sm text-blue-100" data-id="element-902">Files Rapides</p>
                  <p className="text-2xl font-bold" data-id="element-903">
                    {fastQueueStations.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4" data-id="element-904">
              <div className="flex items-center gap-3" data-id="element-905">
                <div className="bg-white bg-opacity-30 p-2 rounded-lg" data-id="element-906">
                  <CarIcon className="w-5 h-5 text-white" data-id="element-907" />
                </div>
                <div data-id="element-908">
                  <p className="text-sm text-blue-100" data-id="element-909">Total Véhicules</p>
                  <p className="text-2xl font-bold" data-id="element-910">{totalVehicles}</p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4" data-id="element-911">
              <div className="flex items-center gap-3" data-id="element-912">
                <div className="bg-white bg-opacity-30 p-2 rounded-lg" data-id="element-913">
                  <TrendingUpIcon className="w-5 h-5 text-white" data-id="element-914" />
                </div>
                <div data-id="element-915">
                  <p className="text-sm text-blue-100" data-id="element-916">Taux d'Occupation</p>
                  <p className="text-2xl font-bold" data-id="element-917">
                    {stations.length > 0 ? Math.round(totalVehicles / stations.length * 10) / 10 : 0}
                    /station
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-380px)]" data-id="element-918">
          {filteredStations.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-xl" data-id="element-919">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-920" />
              <p className="text-gray-500 text-lg" data-id="element-921">Aucune station trouvée</p>
              <p className="text-gray-400 text-sm mt-2" data-id="element-922">
                Essayez de modifier vos critères de recherche
              </p>
            </div> : <>
              {/* Fast Queue Stations Section */}
              {fastQueueStations.length > 0 && <div className="mb-6" data-id="element-923">
                  <div className="flex items-center gap-3 mb-4" data-id="element-924">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg" data-id="element-925">
                      <ZapIcon className="w-6 h-6 text-white" data-id="element-926" />
                    </div>
                    <div data-id="element-927">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-928">
                        Stations à File Rapide
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-929">
                        Mouvement actif dans les 5 dernières minutes - Service
                        fluide
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-930">
                    {fastQueueStations.map(station => {
                const stats = getQueueStats(station.id);
                return <div key={station.id} className="border-2 rounded-xl p-5 transition-all hover:shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 relative" data-id="element-931">
                          {/* Fast Queue Badge */}
                          <div className="absolute top-3 right-3" data-id="element-932">
                            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full flex items-center gap-1 text-xs font-bold shadow-lg" data-id="element-933">
                              <ZapIcon className="w-3 h-3" data-id="element-934" />
                              FILE RAPIDE
                            </div>
                          </div>
                          {/* Station Header */}
                          <div className="flex items-start justify-between mb-4 pr-24" data-id="element-935">
                            <div className="flex-1" data-id="element-936">
                              <div className="flex items-center gap-2 mb-2" data-id="element-937">
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-938">
                                  {station.name}
                                </h3>
                                <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-939" />
                              </div>
                              <p className="text-sm text-gray-600 mb-1" data-id="element-940">
                                {station.location}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-941">
                                {station.commune}
                              </p>
                            </div>
                          </div>
                          {/* Queue Stats */}
                          <div className="bg-white rounded-lg p-4 mb-4 border-2 border-green-200" data-id="element-942">
                            <p className="text-xs font-semibold text-gray-500 mb-3" data-id="element-943">
                              FILE D'ATTENTE
                            </p>
                            <div className="grid grid-cols-3 gap-3" data-id="element-944">
                              <div className="text-center" data-id="element-945">
                                <div className="bg-yellow-100 rounded-lg p-2 mb-1" data-id="element-946">
                                  <ClockIcon className="w-4 h-4 text-yellow-600 mx-auto" data-id="element-947" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-948">
                                  En Attente
                                </p>
                                <p className="text-lg font-bold text-yellow-600" data-id="element-949">
                                  {stats.waiting}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-950">
                                <div className="bg-green-100 rounded-lg p-2 mb-1" data-id="element-951">
                                  <GaugeIcon className="w-4 h-4 text-green-600 mx-auto" data-id="element-952" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-953">
                                  En Service
                                </p>
                                <p className="text-lg font-bold text-green-600" data-id="element-954">
                                  {stats.inService}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-955">
                                <div className="bg-blue-100 rounded-lg p-2 mb-1" data-id="element-956">
                                  <CarIcon className="w-4 h-4 text-blue-600 mx-auto" data-id="element-957" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-958">
                                  Total
                                </p>
                                <p className="text-lg font-bold text-blue-600" data-id="element-959">
                                  {stats.total}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Station Info */}
                          <div className="space-y-2" data-id="element-960">
                            <div className="flex items-center justify-between text-sm" data-id="element-961">
                              <span className="text-gray-600" data-id="element-962">Pompes</span>
                              <span className="font-bold text-gray-900" data-id="element-963">
                                {station.pumps}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm" data-id="element-964">
                              <span className="text-gray-600" data-id="element-965">Capacité</span>
                              <span className="font-bold text-gray-900" data-id="element-966">
                                {(station.capacity / 1000).toFixed(0)}k L
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-sm" data-id="element-967">
                              <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-968" />
                              <span className="text-gray-600 text-xs" data-id="element-969">
                                {station.openingHours}
                              </span>
                            </div>
                          </div>
                          {/* Fuel Availability */}
                          <div className="mt-4 pt-4 border-t border-green-200" data-id="element-970">
                            <p className="text-xs font-semibold text-gray-500 mb-2" data-id="element-971">
                              DISPONIBILITÉ CARBURANT
                            </p>
                            <div className="space-y-2" data-id="element-972">
                              <div className="flex items-center justify-between" data-id="element-973">
                                <div className="flex items-center gap-2" data-id="element-974">
                                  <FuelIcon className="w-4 h-4 text-blue-600" data-id="element-975" />
                                  <span className="text-xs text-gray-700" data-id="element-976">
                                    Essence
                                  </span>
                                </div>
                                {station.fuelAvailability.essence ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-977">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-978">
                                    Rupture
                                  </span>}
                              </div>
                              <div className="flex items-center justify-between" data-id="element-979">
                                <div className="flex items-center gap-2" data-id="element-980">
                                  <FuelIcon className="w-4 h-4 text-orange-600" data-id="element-981" />
                                  <span className="text-xs text-gray-700" data-id="element-982">
                                    Diesel
                                  </span>
                                </div>
                                {station.fuelAvailability.diesel ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-983">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-984">
                                    Rupture
                                  </span>}
                              </div>
                              {station.prices.gpl && <div className="flex items-center justify-between" data-id="element-985">
                                  <div className="flex items-center gap-2" data-id="element-986">
                                    <FuelIcon className="w-4 h-4 text-purple-600" data-id="element-987" />
                                    <span className="text-xs text-gray-700" data-id="element-988">
                                      GPL
                                    </span>
                                  </div>
                                  {station.fuelAvailability.gpl ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-989">
                                      Disponible
                                    </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-990">
                                      Rupture
                                    </span>}
                                </div>}
                            </div>
                          </div>
                          {/* Activity Log */}
                          {getStationActivityLog(station.id).length > 0 && <div className="mt-4 pt-4 border-t border-green-200" data-id="element-991">
                              <p className="text-xs font-semibold text-gray-500 mb-2" data-id="element-992">
                                ACTIVITÉ RÉCENTE
                              </p>
                              <div className="space-y-2" data-id="element-993">
                                {getStationActivityLog(station.id).map((event, idx) => <div key={idx} className="bg-white rounded-md px-2 py-1 text-xs" data-id="element-994">
                                      <div className="flex items-center gap-1 text-gray-700" data-id="element-995">
                                        <UserIcon className="w-3 h-3 text-blue-600" data-id="element-996" />
                                        <span className="font-semibold" data-id="element-997">
                                          {event.userName}
                                        </span>
                                        <span data-id="element-998">a changé le statut</span>
                                      </div>
                                      <div className="text-gray-500 ml-4" data-id="element-999">
                                        {event.timestamp.toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                      </div>
                                    </div>)}
                              </div>
                            </div>}
                        </div>;
              })}
                  </div>
                </div>}
              {/* Regular Stations Section */}
              {regularStations.length > 0 && <div className="mb-6" data-id="element-1000">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1001">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg" data-id="element-1002">
                      <MapPinIcon className="w-6 h-6 text-white" data-id="element-1003" />
                    </div>
                    <div data-id="element-1004">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-1005">
                        Autres Stations Opérationnelles
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-1006">
                        Pas de mouvement récent dans les 5 dernières minutes
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1007">
                    {regularStations.map(station => {
                const stats = getQueueStats(station.id);
                return <div key={station.id} className="border-2 rounded-xl p-5 transition-all hover:shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-id="element-1008">
                          {/* Station Header */}
                          <div className="flex items-start justify-between mb-4" data-id="element-1009">
                            <div className="flex-1" data-id="element-1010">
                              <div className="flex items-center gap-2 mb-2" data-id="element-1011">
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-1012">
                                  {station.name}
                                </h3>
                                <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-1013" />
                              </div>
                              <p className="text-sm text-gray-600 mb-1" data-id="element-1014">
                                {station.location}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1015">
                                {station.commune}
                              </p>
                            </div>
                          </div>
                          {/* Queue Stats */}
                          <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200" data-id="element-1016">
                            <p className="text-xs font-semibold text-gray-500 mb-3" data-id="element-1017">
                              FILE D'ATTENTE
                            </p>
                            <div className="grid grid-cols-3 gap-3" data-id="element-1018">
                              <div className="text-center" data-id="element-1019">
                                <div className="bg-yellow-100 rounded-lg p-2 mb-1" data-id="element-1020">
                                  <ClockIcon className="w-4 h-4 text-yellow-600 mx-auto" data-id="element-1021" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1022">
                                  En Attente
                                </p>
                                <p className="text-lg font-bold text-yellow-600" data-id="element-1023">
                                  {stats.waiting}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-1024">
                                <div className="bg-green-100 rounded-lg p-2 mb-1" data-id="element-1025">
                                  <GaugeIcon className="w-4 h-4 text-green-600 mx-auto" data-id="element-1026" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1027">
                                  En Service
                                </p>
                                <p className="text-lg font-bold text-green-600" data-id="element-1028">
                                  {stats.inService}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-1029">
                                <div className="bg-blue-100 rounded-lg p-2 mb-1" data-id="element-1030">
                                  <CarIcon className="w-4 h-4 text-blue-600 mx-auto" data-id="element-1031" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1032">
                                  Total
                                </p>
                                <p className="text-lg font-bold text-blue-600" data-id="element-1033">
                                  {stats.total}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Station Info */}
                          <div className="space-y-2" data-id="element-1034">
                            <div className="flex items-center justify-between text-sm" data-id="element-1035">
                              <span className="text-gray-600" data-id="element-1036">Pompes</span>
                              <span className="font-bold text-gray-900" data-id="element-1037">
                                {station.pumps}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm" data-id="element-1038">
                              <span className="text-gray-600" data-id="element-1039">Capacité</span>
                              <span className="font-bold text-gray-900" data-id="element-1040">
                                {(station.capacity / 1000).toFixed(0)}k L
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-sm" data-id="element-1041">
                              <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-1042" />
                              <span className="text-gray-600 text-xs" data-id="element-1043">
                                {station.openingHours}
                              </span>
                            </div>
                          </div>
                          {/* Fuel Availability */}
                          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1044">
                            <p className="text-xs font-semibold text-gray-500 mb-2" data-id="element-1045">
                              DISPONIBILITÉ CARBURANT
                            </p>
                            <div className="space-y-2" data-id="element-1046">
                              <div className="flex items-center justify-between" data-id="element-1047">
                                <div className="flex items-center gap-2" data-id="element-1048">
                                  <FuelIcon className="w-4 h-4 text-blue-600" data-id="element-1049" />
                                  <span className="text-xs text-gray-700" data-id="element-1050">
                                    Essence
                                  </span>
                                </div>
                                {station.fuelAvailability.essence ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1051">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1052">
                                    Rupture
                                  </span>}
                              </div>
                              <div className="flex items-center justify-between" data-id="element-1053">
                                <div className="flex items-center gap-2" data-id="element-1054">
                                  <FuelIcon className="w-4 h-4 text-orange-600" data-id="element-1055" />
                                  <span className="text-xs text-gray-700" data-id="element-1056">
                                    Diesel
                                  </span>
                                </div>
                                {station.fuelAvailability.diesel ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1057">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1058">
                                    Rupture
                                  </span>}
                              </div>
                              {station.prices.gpl && <div className="flex items-center justify-between" data-id="element-1059">
                                  <div className="flex items-center gap-2" data-id="element-1060">
                                    <FuelIcon className="w-4 h-4 text-purple-600" data-id="element-1061" />
                                    <span className="text-xs text-gray-700" data-id="element-1062">
                                      GPL
                                    </span>
                                  </div>
                                  {station.fuelAvailability.gpl ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1063">
                                      Disponible
                                    </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1064">
                                      Rupture
                                    </span>}
                                </div>}
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </div>}
              {/* Non-Operational Stations Section */}
              {nonOperationalStations.length > 0 && <div data-id="element-1065">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1066">
                    <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-2 rounded-lg" data-id="element-1067">
                      <AlertCircleIcon className="w-6 h-6 text-white" data-id="element-1068" />
                    </div>
                    <div data-id="element-1069">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-1070">
                        Stations Hors Service
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-1071">
                        Actuellement non opérationnelles
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1072">
                    {nonOperationalStations.map(station => {
                const stats = getQueueStats(station.id);
                return <div key={station.id} className="border-2 rounded-xl p-5 transition-all hover:shadow-lg bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300" data-id="element-1073">
                          {/* Station Header */}
                          <div className="flex items-start justify-between mb-4" data-id="element-1074">
                            <div className="flex-1" data-id="element-1075">
                              <div className="flex items-center gap-2 mb-2" data-id="element-1076">
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-1077">
                                  {station.name}
                                </h3>
                                <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1078" />
                              </div>
                              <p className="text-sm text-gray-600 mb-1" data-id="element-1079">
                                {station.location}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1080">
                                {station.commune}
                              </p>
                            </div>
                          </div>
                          {/* Queue Stats */}
                          <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200" data-id="element-1081">
                            <p className="text-xs font-semibold text-gray-500 mb-3" data-id="element-1082">
                              FILE D'ATTENTE
                            </p>
                            <div className="grid grid-cols-3 gap-3" data-id="element-1083">
                              <div className="text-center" data-id="element-1084">
                                <div className="bg-yellow-100 rounded-lg p-2 mb-1" data-id="element-1085">
                                  <ClockIcon className="w-4 h-4 text-yellow-600 mx-auto" data-id="element-1086" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1087">
                                  En Attente
                                </p>
                                <p className="text-lg font-bold text-yellow-600" data-id="element-1088">
                                  {stats.waiting}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-1089">
                                <div className="bg-green-100 rounded-lg p-2 mb-1" data-id="element-1090">
                                  <GaugeIcon className="w-4 h-4 text-green-600 mx-auto" data-id="element-1091" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1092">
                                  En Service
                                </p>
                                <p className="text-lg font-bold text-green-600" data-id="element-1093">
                                  {stats.inService}
                                </p>
                              </div>
                              <div className="text-center" data-id="element-1094">
                                <div className="bg-blue-100 rounded-lg p-2 mb-1" data-id="element-1095">
                                  <CarIcon className="w-4 h-4 text-blue-600 mx-auto" data-id="element-1096" />
                                </div>
                                <p className="text-xs text-gray-600 mb-1" data-id="element-1097">
                                  Total
                                </p>
                                <p className="text-lg font-bold text-blue-600" data-id="element-1098">
                                  {stats.total}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Station Info */}
                          <div className="space-y-2" data-id="element-1099">
                            <div className="flex items-center justify-between text-sm" data-id="element-1100">
                              <span className="text-gray-600" data-id="element-1101">Pompes</span>
                              <span className="font-bold text-gray-900" data-id="element-1102">
                                {station.pumps}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm" data-id="element-1103">
                              <span className="text-gray-600" data-id="element-1104">Capacité</span>
                              <span className="font-bold text-gray-900" data-id="element-1105">
                                {(station.capacity / 1000).toFixed(0)}k L
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-sm" data-id="element-1106">
                              <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-1107" />
                              <span className="text-gray-600 text-xs" data-id="element-1108">
                                {station.openingHours}
                              </span>
                            </div>
                          </div>
                          {/* Fuel Availability */}
                          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1109">
                            <p className="text-xs font-semibold text-gray-500 mb-2" data-id="element-1110">
                              DISPONIBILITÉ CARBURANT
                            </p>
                            <div className="space-y-2" data-id="element-1111">
                              <div className="flex items-center justify-between" data-id="element-1112">
                                <div className="flex items-center gap-2" data-id="element-1113">
                                  <FuelIcon className="w-4 h-4 text-blue-600" data-id="element-1114" />
                                  <span className="text-xs text-gray-700" data-id="element-1115">
                                    Essence
                                  </span>
                                </div>
                                {station.fuelAvailability.essence ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1116">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1117">
                                    Rupture
                                  </span>}
                              </div>
                              <div className="flex items-center justify-between" data-id="element-1118">
                                <div className="flex items-center gap-2" data-id="element-1119">
                                  <FuelIcon className="w-4 h-4 text-orange-600" data-id="element-1120" />
                                  <span className="text-xs text-gray-700" data-id="element-1121">
                                    Diesel
                                  </span>
                                </div>
                                {station.fuelAvailability.diesel ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1122">
                                    Disponible
                                  </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1123">
                                    Rupture
                                  </span>}
                              </div>
                              {station.prices.gpl && <div className="flex items-center justify-between" data-id="element-1124">
                                  <div className="flex items-center gap-2" data-id="element-1125">
                                    <FuelIcon className="w-4 h-4 text-purple-600" data-id="element-1126" />
                                    <span className="text-xs text-gray-700" data-id="element-1127">
                                      GPL
                                    </span>
                                  </div>
                                  {station.fuelAvailability.gpl ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded" data-id="element-1128">
                                      Disponible
                                    </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded" data-id="element-1129">
                                      Rupture
                                    </span>}
                                </div>}
                            </div>
                          </div>
                          {/* Status Badge */}
                          <div className="mt-4 bg-red-100 border border-red-300 rounded-lg px-3 py-2" data-id="element-1130">
                            <p className="text-xs font-bold text-red-800 text-center" data-id="element-1131">
                              ⚠️ Station Hors Service
                            </p>
                          </div>
                        </div>;
              })}
                  </div>
                </div>}
            </>}
        </div>
      </div>
    </div>;
}